import { useState } from 'react';
import { useLocale } from '../../hooks/useLocale';
import { useTheme } from '../../hooks/useTheme';
import { UaePassFingerprintIcon } from '../uae-pass/UaePassFingerprintIcon';

type SocialProvider = 'google' | 'apple' | 'uae-pass';

interface SocialLoginButtonProps {
  provider: SocialProvider;
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Brand SVG Icons
// ---------------------------------------------------------------------------
export function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function AppleIcon({
  size = 20,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M48.644 32.41c-.07-7.717 6.309-11.425 6.596-11.607-3.589-5.25-9.168-5.965-11.16-6.049-4.745-.484-9.266 2.796-11.677 2.796s-6.127-2.726-10.066-2.65c-5.18.078-9.954 3.007-12.624 7.647-5.377 9.322-1.374 23.151 3.87 30.728 2.565 3.7 5.614 7.871 9.63 7.717 3.87-.154 5.328-2.502 10.001-2.502 4.664 0 5.986 2.502 10.065 2.425 4.157-.084 6.792-3.785 9.337-7.5 2.937-4.296 4.149-8.46 4.22-8.677-.091-.035-8.103-3.105-8.192-12.328"
        fill={color}
      />
      <path
        d="M40.969 9.743c2.13-2.58 3.567-6.168 3.175-9.743-3.07.126-6.785 2.047-8.986 4.626-1.97 2.278-3.701 5.93-3.232 9.434 3.414.266 6.911-1.745 9.043-4.317"
        fill={color}
      />
    </svg>
  );
}

function Spinner({ color }: { color: string }) {
  return (
    <span
      style={{
        width: 16,
        height: 16,
        border: `2px solid ${color}33`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        display: 'inline-block',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Social Login Button
// ---------------------------------------------------------------------------
export function SocialLoginButton({
  provider,
  label,
  onPress,
  loading,
  disabled,
}: SocialLoginButtonProps) {
  const { isRTL } = useLocale();
  const { isDark } = useTheme();
  const [hovered, setHovered] = useState(false);
  const isDisabled = loading || disabled;
  const isUaePass = provider === 'uae-pass';
  const uaePassIconSurface: 'light' | 'dark' = isDark ? 'light' : 'dark';

  const renderIcon = () => {
    if (loading) {
      return <Spinner color={isUaePass ? 'currentColor' : 'var(--text-secondary)'} />;
    }
    switch (provider) {
      case 'google':
        return <GoogleIcon />;
      case 'apple':
        return <AppleIcon />;
      case 'uae-pass':
        return <UaePassFingerprintIcon size={20} surface={uaePassIconSurface} />;
    }
  };

  const baseStyle: React.CSSProperties = isUaePass
    ? {
        backgroundColor: 'var(--text-primary)',
        color: 'var(--bg)',
        border: 0,
      }
    : {
        backgroundColor: hovered ? 'rgba(0,0,0,0.04)' : 'var(--input-bg)',
        color: 'var(--text-primary)',
        border: '1px solid var(--input-border)',
        boxShadow: hovered ? 'none' : 'var(--gem-input-shadow)',
      };

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onPress}
      disabled={isDisabled}
      onMouseEnter={() => {
        if (!isDisabled) setHovered(true);
      }}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        height: 52,
        borderRadius: 9999,
        paddingInline: 18,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontSize: 15,
        fontWeight: 500,
        lineHeight: '20px',
        cursor: isDisabled ? 'default' : 'pointer',
        opacity: isDisabled && !loading ? 0.4 : 1,
        fontFamily: 'inherit',
        transition: 'background-color 150ms, opacity 150ms',
        boxSizing: 'border-box',
        direction: isRTL ? 'rtl' : 'ltr',
        flexDirection: 'row',
        ...baseStyle,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 22,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {renderIcon()}
      </span>
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
    </button>
  );
}
